/**
 * Ein `<Link>`, dessen Ziel aus einer `Ansicht` gebaut wird - damit nirgends im Markup eine URL von
 * Hand zusammengesetzt wird und `adresse.ts` die einzige Stelle bleibt, die die Grammatik kennt.
 *
 * Der Grund, warum es ein ANKER sein muss und kein `onClick`: nur ein echtes `<a href>` kennt
 * Rechtsklick -> "Link-Adresse kopieren" und Strg+Klick -> neuer Tab. Genau darum ging es dem
 * Auftraggeber ("ich moechte links verschicken koennen") - ohne Anker muesste er jede Bestellung
 * erst oeffnen, um an ihren Link zu kommen.
 */
import type { JSX, MouseEvent, ReactNode } from "react";
import { Link } from "react-router";
import { schreiben, type Ansicht } from "../state/adresse.ts";

/**
 * Ein "einfacher" Klick: linke Maustaste, keine Zusatztaste gedrueckt (Befund W-1 der Pruefrunde 1
 * zu Aufgabe 5). NUR bei einem solchen Klick soll ein begleitendes `onClick` (siehe unten) die
 * AKTUELLE Ansicht aendern - bei Strg/Cmd/Shift/Mittelklick will der Nutzer die Bestellung in einem
 * NEUEN Tab sehen, nicht auch noch in diesem hier.
 *
 * `react-router`s `<Link>` uebergeht bei genau diesen Ereignissen die EIGENE Navigation bereits von
 * sich aus (der Browser oeffnet den neuen Tab dann nativ ueber `href`) - das schuetzt aber nur die
 * Link-Navigation selbst, NICHT ein zusaetzliches `onClick`, das am selben Klick haengt. Ohne diese
 * Pruefung feuert ein begleitender Handler (z. B. `onOeffnen` an den sechs Oeffnen-Stellen) bei
 * JEDEM Klick, Zusatztaste hin oder her - gemessen: Strg+Klick auf eine Handlungszeile oeffnete
 * einen neuen Tab UND riss die aktuelle Ansicht auf die angeklickte Bestellung um.
 */
export function istEinfacherKlick(ereignis: MouseEvent): boolean {
  return ereignis.button === 0 && !ereignis.metaKey && !ereignis.ctrlKey && !ereignis.shiftKey && !ereignis.altKey;
}

export interface VerweisProps {
  ziel: Ansicht;
  className?: string;
  /** Durchreichung an den Anker - z. B. `Navigation.tsx`s `id="nav-${seite}"`, an die im dunklen
   *  Modus die reiterspezifische Aktivfarbe haengt (`#nav-board.active` u.ae., `bestand.css`). */
  id?: string;
  /**
   * Vorgabe `false` (schieben, ein neuer Verlaufseintrag - das braucht z. B. `Navigation.tsx`s
   * Reiter, wo der Anker die EINZIGE Navigation ist).
   *
   * `true` fuer die sechs/sieben Oeffnen-Stellen: ihr begleitendes `onClick` (siehe unten, oder das
   * `onClick` des umschlossenen Knopfs) fuehrt DIESELBE Navigation bereits einmal aus (`onOeffnen` ->
   * `App.tsx#drawerOeffnen`/`uebersichtOeffnen` -> `setzeAnsicht`, EIN Verlaufseintrag) - ohne
   * `replace` schoebe der Anker hinterher NOCHMAL denselben Eintrag (gemessen: `adresse.test.tsx`,
   * "Klick auf eine Bestellung im Audit-Protokoll erzeugt GENAU EINEN Verlaufseintrag", Befund W-3
   * der ersten Pruefrunde). `replace` macht aus dem zweiten Eintrag ein wirkungsloses Ueberschreiben
   * DESSELBEN Ziels.
   */
  replace?: boolean;
  /**
   * Ein zusaetzlicher Klick-Handler, der GEMEINSAM mit der Navigation des Ankers feuert (react-router
   * ruft ihn vor der eigenen Navigationslogik auf, siehe `Link`). Gedacht fuer Faelle, in denen der
   * Anker selbst der einzige Traeger einer Wirkung ist, die sonst an einem `onClick` haengt, das der
   * Anker durch seine Platzierung UMGEHT - `Bestellzeile.tsx`s Bestellnummer-Zelle etwa: dort schluckt
   * `Bearbeitbar`s `stopPropagation` den Zeilen-`onClick` (`drawerOeffnen`, inklusive
   * `versende({art:"geoeffnet"})` fuer die SSE-Konflikterkennung) fuer JEDEN Klick auf die Zelle,
   * unabhaengig vom Anker - ohne dieses Prop bliebe die SSE-Erkennung fuer genau diese Bestellung
   * unscharf (Befund K-1 der Pruefrunde 1). Der Aufrufer ist selbst fuer `istEinfacherKlick` (oben)
   * verantwortlich, falls die Wirkung nur bei einem einfachen Klick laufen soll.
   */
  onClick?: (ereignis: MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
}

export function Verweis({ ziel, className, id, replace, onClick, children }: VerweisProps): JSX.Element {
  return (
    <Link to={schreiben(ziel)} className={className} id={id} replace={replace} onClick={onClick}>
      {children}
    </Link>
  );
}
